'use client';

import './pagination.scss';

import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { ARTICLE_URL_PARAMS } from '@sola/schema/lib/schema.js';

const ReactPaginate = dynamic(() => import('react-paginate'), { ssr: false });

export const PaginationProps = z.object({
  totalItems: z.number().min(1),
  itemsPerPage: z.number().min(1),
  initialPage: z.number().min(1).optional(),
});
export type PaginationProps = z.infer<typeof PaginationProps> & {
  onPageChange: (page: number) => void;
};

const InternalPagination: React.FC<PaginationProps> = ({
  itemsPerPage,
  totalItems,
  initialPage = 0,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const params = useMemo(
    () => new URLSearchParams(searchParams?.toString()),
    [searchParams],
  );

  const pageCount = Math.ceil(totalItems / itemsPerPage);
  const [currentPage, setCurrentPage] = useState<number>(
    params.get(ARTICLE_URL_PARAMS.PAGE)
      ? Number(params.get(ARTICLE_URL_PARAMS.PAGE))
      : initialPage,
  );

  const handlePageClick = (activePage: number) => {
    setCurrentPage(activePage);
    const cleanedActivePage = Number.isNaN(activePage) ? 0 : activePage;
    if (currentPage === cleanedActivePage) {
      return;
    }
    onPageChange(cleanedActivePage);
  };

  useEffect(() => {
    setCurrentPage(Number(params.get(ARTICLE_URL_PARAMS.PAGE)));
  }, [params.get(ARTICLE_URL_PARAMS.PAGE)]);

  const getPrevButton = () => (
    <button
      className="Pagination__adjacent Pagination__adjacent--prev label-potent label-md"
      type="button"
      disabled={currentPage === 0}
      onClick={() => {
        handlePageClick(currentPage - 1);
      }}
    >
      <ArkIcon iconName="arrow-left" size="20" />
      <span>{t('skippy.article.pagination.previous')}</span>
    </button>
  );

  const getNextButton = () => (
    <button
      className="Pagination__adjacent Pagination__adjacent--next label-potent label-md"
      type="button"
      disabled={currentPage === pageCount - 1}
      onClick={() => {
        handlePageClick(currentPage + 1);
      }}
    >
      <span>{t('skippy.article.pagination.next')}</span>
      <ArkIcon iconName="arrow-right" size="20" />
    </button>
  );

  return (
    <div className="Pagination">
      <ReactPaginate
        breakLabel="..."
        breakClassName="text-center"
        nextLabel={getNextButton()}
        nextLinkClassName="Pagination__adjacent-link Pagination__adjacent-link--next label-potent label-md"
        nextClassName="Pagination__adjacent Pagination__adjacent--next"
        onPageChange={(v) => handlePageClick(v.selected)}
        pageRangeDisplayed={2}
        marginPagesDisplayed={1}
        pageCount={pageCount}
        forcePage={currentPage}
        previousLabel={getPrevButton()}
        renderOnZeroPageCount={null}
        containerClassName="Pagination__list"
        previousClassName="Pagination__adjacent Pagination__adjacent--prev"
        previousLinkClassName="Pagination__adjacent-link Pagination__adjacent-link--prev label-potent label-md"
        pageClassName="Pagination__page"
        pageLinkClassName="ButtonStyle"
      />
    </div>
  );
};

const PaginationSkeleton = () => (
  <div className="Pagination">
    <div className="Pagination__list">
      <div className="Pagination__adjacent Pagination__adjacent--prev">
        <Skeleton height={20} width={20} borderRadius="50%" />
        <span>
          <Skeleton height={16} width={60} />
        </span>
      </div>

      <div className="flex gap-snug-sm">
        <Skeleton height={48} width={48} borderRadius="0.5rem" />
        <Skeleton height={48} width={48} borderRadius="0.5rem" />
        <Skeleton height={48} width={48} borderRadius="0.5rem" />
      </div>

      <div className="Pagination__adjacent Pagination__adjacent--next">
        <span>
          <Skeleton height={16} width={40} />
        </span>
        <Skeleton height={20} width={20} borderRadius="50%" />
      </div>
    </div>
  </div>
);

export const Pagination: React.FC<PaginationProps> = (props) => (
  <Suspense fallback={<PaginationSkeleton />}>
    <InternalPagination {...props} />
  </Suspense>
);
